import { z } from 'zod'
import { userRoomVisibilityValues } from './live-room-user'

/** The longest note someone may add when asking to join a room on request. */
export const ROOM_REQUEST_NOTE_MAX = 200

/** Digits of the code that connects an organizer account to a Pulsify profile. */
export const ORGANIZER_LINK_CODE_LENGTH = 6

/** Minutes an organizer link code stays valid. */
export const ORGANIZER_LINK_CODE_MINUTES = 10

/**
 * Who may get into an organizer's event room: `request` ("Auf Anfrage") lists it for everyone
 * but lets people in only once the organizer accepted them; null keeps today's open room.
 */
export const eventRoomVisibilityValues = ['request'] as const
export type EventRoomVisibility = (typeof eventRoomVisibilityValues)[number]

export const roomRequestStatusValues = ['pending', 'accepted', 'declined', 'withdrawn'] as const
export type RoomRequestStatus = (typeof roomRequestStatusValues)[number]

/**
 * Where the viewer stands at a room on request: `none` never asked (or withdrew), `pending`
 * waits for the host (a quiet decline also reads as pending), `accepted` is in, `host` runs it.
 */
export const myRoomRequestStateValues = ['none', 'pending', 'accepted', 'host'] as const
export type MyRoomRequestState = (typeof myRoomRequestStateValues)[number]

/** The most companions anyone may bring; an event allows up to its own `plusOnesMax`. */
export const ROOM_REQUEST_COMPANIONS_MAX = 10

export const roomRequestCreateSchema = z.object({
  note: z.string().trim().max(ROOM_REQUEST_NOTE_MAX).optional(),
  /** People the asker brings along ("Begleitung"); at most the event's `companionsMax`. */
  companions: z.number().int().min(0).max(ROOM_REQUEST_COMPANIONS_MAX).optional(),
})
export type RoomRequestCreateInput = z.infer<typeof roomRequestCreateSchema>

export const myRoomRequestSchema = z.object({
  state: z.enum(myRoomRequestStateValues),
  note: z.string().nullable(),
  requestedAt: z.string().nullable(),
  /** "Mich Anfragenden zeigen": off, people outside the room only count the viewer. */
  shownToOutsiders: z.boolean(),
  /** Companions on the viewer's request; 0 when they did not ask. */
  companions: z.number().int().nonnegative(),
  /** How many companions the host allows per request; 0 means none. */
  companionsMax: z.number().int().nonnegative(),
  /**
   * Places still free (people incl. companions) when the host set a limit; null without one.
   * At 0 new requests land on the waiting list: they stay pending until a place is free.
   */
  placesLeft: z.number().int().nonnegative().nullable(),
})
export type MyRoomRequest = z.infer<typeof myRoomRequestSchema>

export const myRoomRequestResponseSchema = z.object({ request: myRoomRequestSchema })
export type MyRoomRequestResponse = z.infer<typeof myRoomRequestResponseSchema>

export const roomRequestSettingsSchema = z.object({ shownToOutsiders: z.boolean() })
export type RoomRequestSettingsInput = z.infer<typeof roomRequestSettingsSchema>

/** One request as the host sees it. */
export const roomJoinRequestSchema = z.object({
  id: z.string().uuid(),
  eventId: z.string().uuid(),
  userId: z.string().uuid(),
  displayName: z.string().nullable(),
  firstName: z.string(),
  username: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  /** Only when the person shows their age. */
  age: z.number().int().nullable(),
  /** Instagram and LinkedIn handles, as profiles show them (18+ only). */
  instagram: z.string().nullable(),
  linkedin: z.string().nullable(),
  note: z.string().nullable(),
  /** People this person brings along. */
  companions: z.number().int().nonnegative(),
  status: z.enum(roomRequestStatusValues),
  isFriend: z.boolean(),
  createdAt: z.string(),
  answeredAt: z.string().nullable(),
})
export type RoomJoinRequest = z.infer<typeof roomJoinRequestSchema>

export const roomRequestListQuerySchema = z.object({
  status: z.enum(['pending', 'accepted', 'declined']).default('pending'),
})
export type RoomRequestListQuery = z.infer<typeof roomRequestListQuerySchema>

export const roomJoinRequestsResponseSchema = z.object({
  requests: z.array(roomJoinRequestSchema),
  counts: z.object({
    pending: z.number().int().nonnegative(),
    accepted: z.number().int().nonnegative(),
    declined: z.number().int().nonnegative(),
  }),
  /** Accepted people including their companions. */
  acceptedPeople: z.number().int().nonnegative(),
  /** The host's limit of people (the event's capacity); null without one. */
  capacity: z.number().int().nonnegative().nullable(),
})
export type RoomJoinRequestsResponse = z.infer<typeof roomJoinRequestsResponseSchema>

export const roomJoinRequestResponseSchema = z.object({ request: roomJoinRequestSchema })
export type RoomJoinRequestResponse = z.infer<typeof roomJoinRequestResponseSchema>

/**
 * A room the viewer runs, for "Veranstaltet": their own user rooms, their linked organizers'
 * events and the running venue rooms of those organizers' venues.
 */
export const hostedRoomSchema = z.object({
  eventId: z.string().uuid(),
  kind: z.enum(['room', 'event']),
  title: z.string(),
  imageUrl: z.string().nullable(),
  /** Same picture as `imageUrl`: the event cover or the room's cover photo. */
  coverUrl: z.string().nullable(),
  startTime: z.string(),
  endTime: z.string(),
  live: z.boolean(),
  /** A user room's visibility; null for an organizer event. */
  visibility: z.enum(userRoomVisibilityValues).nullable(),
  onRequest: z.boolean(),
  /** People checked in right now. */
  here: z.number().int().nonnegative(),
  /** Open requests; always 0 unless `onRequest`. */
  pendingRequests: z.number().int().nonnegative(),
  /** The organizer behind an event; null for the viewer's own room. */
  businessId: z.string().uuid().nullable(),
  businessName: z.string().nullable(),
  /** Tonight's room at a venue of a linked organizer; `kind` stays 'room' and `title` is the venue. */
  venueRoom: z.boolean().default(false),
  /** The event sells Pulsify tickets, so the door can scan them in the app. */
  sellsTickets: z.boolean().default(false),
})
export type HostedRoom = z.infer<typeof hostedRoomSchema>

export const hostedRoomsResponseSchema = z.object({ rooms: z.array(hostedRoomSchema) })
export type HostedRoomsResponse = z.infer<typeof hostedRoomsResponseSchema>

/** The six digits the app shows under "Mit Veranstalter-Konto verbinden". */
export const organizerLinkCodeResponseSchema = z.object({
  code: z.string().regex(/^\d{6}$/),
  expiresAt: z.string(),
})
export type OrganizerLinkCodeResponse = z.infer<typeof organizerLinkCodeResponseSchema>

/** An organizer account the viewer's profile is connected to. */
export const organizerLinkSchema = z.object({
  businessId: z.string().uuid(),
  businessName: z.string(),
  linkedAt: z.string(),
})
export type OrganizerLink = z.infer<typeof organizerLinkSchema>

export const organizerLinksResponseSchema = z.object({ links: z.array(organizerLinkSchema) })
export type OrganizerLinksResponse = z.infer<typeof organizerLinksResponseSchema>

export const businessProfileLinkCreateSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/),
})
export type BusinessProfileLinkCreateInput = z.infer<typeof businessProfileLinkCreateSchema>

/** A Pulsify profile connected to the business, as the dashboard lists it. */
export const businessProfileLinkSchema = z.object({
  userProfileId: z.string().uuid(),
  displayName: z.string().nullable(),
  username: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  linkedAt: z.string(),
})
export type BusinessProfileLink = z.infer<typeof businessProfileLinkSchema>

export const businessProfileLinksResponseSchema = z.object({
  links: z.array(businessProfileLinkSchema),
})
export type BusinessProfileLinksResponse = z.infer<typeof businessProfileLinksResponseSchema>

export const businessProfileLinkResponseSchema = z.object({ link: businessProfileLinkSchema })
export type BusinessProfileLinkResponse = z.infer<typeof businessProfileLinkResponseSchema>
